import React from 'react';
import { clsx } from 'clsx';

interface SpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'white' | 'gray' | 'current';
  className?: string;
  label?: string;
}

export function Spinner({
  size = 'md',
  color = 'primary',
  className,
  label,
}: SpinnerProps) {
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12',
  };

  const colorClasses = {
    primary: 'text-blue-600',
    white: 'text-white',
    gray: 'text-gray-400 dark:text-gray-500',
    current: 'text-current',
  };

  return (
    <div className={clsx('inline-flex items-center', className)} role="status">
      <svg
        className={clsx('animate-spin', sizeClasses[size], colorClasses[color])}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      {label && (
        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">{label}</span>
      )}
      <span className="sr-only">{label || 'Loading...'}</span>
    </div>
  );
}

interface LoadingOverlayProps {
  visible: boolean;
  label?: string;
  blur?: boolean;
  children?: React.ReactNode;
}

export function LoadingOverlay({
  visible,
  label,
  blur = true,
  children,
}: LoadingOverlayProps) {
  if (!visible) return <>{children}</>;

  return (
    <div className="relative">
      {children}
      <div
        className={clsx(
          'absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-800',
          blur && 'backdrop-blur-sm'
        )}
      >
        <Spinner size="lg" label={label} />
      </div>
    </div>
  );
}
